import { Injectable, inject, signal, PLATFORM_ID } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { isPlatformBrowser } from '@angular/common';
import type { Stripe, StripeCardElement, StripeElements } from '@stripe/stripe-js';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../../environments/environment';
import { AffiliateTrackingService } from '../../core/services/affiliate-tracking.service';

@Injectable({
  providedIn: 'root'
})
export class PaymentService {
  private readonly http = inject(HttpClient);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly affiliateTracking = inject(AffiliateTrackingService);

  private stripe: Stripe | null = null;
  private elements: StripeElements | null = null;
  private cardElement: StripeCardElement | null = null;

  readonly paymentStatus = signal<'idle' | 'processing' | 'success' | 'error'>('idle');
  readonly paymentError = signal<string | null>(null);
  readonly amount = signal<number | null>(null);
  readonly clientSecret = signal<string | null>(null);
  readonly isMock = signal<boolean>(false);

  readonly mockCardNumber = signal<string>('4242 4242 4242 4242');
  readonly mockCardExpiry = signal<string>('12/28');
  readonly mockCardCvc = signal<string>('123');

  private async getStripe(): Promise<Stripe | null> {
    if (!isPlatformBrowser(this.platformId)) return null;
    if (this.stripe) return this.stripe;

    try {
      const { loadStripe } = await import('@stripe/stripe-js');
      const config = await firstValueFrom(
        this.http.get<{ publishableKey: string }>(`${environment.apiBaseUrl}/api/payment/config`)
      );
      if (!config.publishableKey || config.publishableKey.trim() === '') {
        return null;
      }
      this.stripe = await loadStripe(config.publishableKey);
      return this.stripe;
    } catch {
      return null;
    }
  }

  async createPaymentIntent(serviceId: string, fallbackAmountCents = 15000): Promise<void> {
    this.paymentStatus.set('idle');
    this.paymentError.set(null);
    this.clientSecret.set(null);
    this.amount.set(null);
    this.isMock.set(false);

    const affiliateId = this.affiliateTracking.getActiveAffiliateId();
    const referralToken = this.affiliateTracking.getActiveReferralToken();

    try {
      const res = await firstValueFrom(
        this.http.post<{ clientSecret: string; amount: number; isMock?: boolean }>(
          `${environment.apiBaseUrl}/api/payment/create-intent`,
          { serviceId, affiliateId, referralToken }
        )
      );
      this.clientSecret.set(res.clientSecret);
      this.amount.set(res.amount);
      if (res.isMock || res.clientSecret.startsWith('pi_mock_')) {
        this.isMock.set(true);
      }
    } catch {
      console.warn('[PaymentService] Endpoint unavailable or failed, enabling mock mode');
      this.isMock.set(true);
      this.clientSecret.set(`pi_mock_${Date.now()}_secret_mock`);
      this.amount.set(fallbackAmountCents);
    }
  }

  async mountCardElement(container: HTMLElement): Promise<StripeCardElement | null> {
    if (this.isMock()) return null;

    try {
      const stripe = await this.getStripe();
      if (!stripe) {
        this.isMock.set(true);
        return null;
      }

      if (this.cardElement) {
        this.cardElement.unmount();
        this.cardElement = null;
      }

      this.elements = stripe.elements();
      this.cardElement = this.elements.create('card', {
        hidePostalCode: true,
        style: {
          base: {
            fontFamily: '"Public Sans", sans-serif',
            fontSize: '16px',
            color: '#1a1c1c',
            '::placeholder': { color: '#9ca3af' },
          },
          invalid: { color: '#dc2626' },
        },
      });
      this.cardElement.mount(container);
      return this.cardElement;
    } catch (err) {
      console.warn('[PaymentService] Failed mounting Stripe Card Element, falling back to mock UI:', err);
      this.isMock.set(true);
      return null;
    }
  }

  async confirmPayment(): Promise<boolean> {
    if (this.isMock()) {
      this.paymentStatus.set('processing');
      await new Promise(resolve => setTimeout(resolve, 600));
      const cleanCard = this.mockCardNumber().replace(/\s+/g, '');
      if (cleanCard.endsWith('0002') || cleanCard === '4000000000000002') {
        this.paymentStatus.set('error');
        this.paymentError.set('Your card was declined. Please try another card.');
        return false;
      }
      this.paymentStatus.set('success');
      return true;
    }

    const stripe = await this.getStripe();
    const secret = this.clientSecret();
    if (!stripe || !secret || !this.cardElement) {
      this.paymentStatus.set('error');
      this.paymentError.set('Payment component is not ready. Please refresh the page and try again.');
      return false;
    }

    this.paymentStatus.set('processing');
    this.paymentError.set(null);

    try {
      const { error, paymentIntent } = await stripe.confirmCardPayment(secret, {
        payment_method: { card: this.cardElement },
      });

      if (error) {
        this.paymentStatus.set('error');
        this.paymentError.set(error.message ?? 'Payment failed. Please try again.');
        return false;
      }

      if (paymentIntent?.status === 'succeeded') {
        this.paymentStatus.set('success');
        return true;
      }

      this.paymentStatus.set('error');
      this.paymentError.set('Unexpected payment status. Please contact support.');
      return false;
    } catch (err: any) {
      this.paymentStatus.set('error');
      this.paymentError.set(err?.message || 'Payment processing error. Please try again.');
      return false;
    }
  }

  getPaymentIntentId(): string | null {
    // Extracted from the clientSecret — format: pi_xxx_secret_yyy
    const secret = this.clientSecret();
    return secret ? secret.split('_secret_')[0] : null;
  }

  reset(): void {
    if (this.cardElement) {
      try {
        this.cardElement.unmount();
      } catch {}
      this.cardElement = null;
    }
    this.elements = null;
    this.paymentStatus.set('idle');
    this.paymentError.set(null);
    this.amount.set(null);
    this.clientSecret.set(null);
    this.isMock.set(false);
  }
}

